import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow, updateRows } from "@/lib/adminApi";
import { STORY_STATUS, optionsFrom, type FarmerStory, type GalleryItem } from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { CoverPicker, GalleryPicker } from "@/pages/admin/components/MediaPicker";
import { GalleryCell } from "@/pages/admin/components/GalleryCell";
import { contentStatusMeta, formatSchedule, toDateTimeLocal } from "@/lib/publishSchedule";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { isStoragePath } from "@/lib/mediaStorage";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_STORY = {
  title: "",
  farmer_name: "",
  farm_name: "",
  village: "",
  category: "Farm Life",
  excerpt: "",
  content: "",
  cover_image: "",
  read_time: "4 min read",
  gallery: [] as GalleryItem[],
  featured: false,
  status: "draft",
  scheduled_for: "",
};

export default function AdminFarmerStories() {
  const [rows, setRows] = useState<FarmerStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_STORY });
  const [coverPreview, setCoverPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<FarmerStory>("farmer_stories", "title"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load farmer stories.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const galleryMap = useAssetUrlMap(
    rows.flatMap((row) => (row.gallery || []).map((item) => item.src)),
  );

  const update = async (id: number, patch: Partial<FarmerStory>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farmer_stories", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.filter((row) => row.id !== id));
    setSelectedId(null);
    try {
      await deleteRow("farmer_stories", id);
      notify(`${current?.title || "Story"} deleted.`);
    } catch {
      setError("We couldn't delete that story. Please try again.");
      load();
    }
  };

  const createStory = async () => {
    if (!draft.title.trim()) {
      setError("A story title is required.");
      return;
    }
    if (draft.status === "scheduled" && !draft.scheduled_for) {
      setError("Pick a date and time to schedule this story.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<FarmerStory>("farmer_stories", {
        title: draft.title.trim(),
        farmer_name: draft.farmer_name || null,
        farm_name: draft.farm_name || null,
        village: draft.village || null,
        category: draft.category || null,
        excerpt: draft.excerpt,
        content: draft.content,
        cover_image: draft.cover_image || null,
        read_time: draft.read_time || null,
        gallery: draft.gallery,
        featured: draft.featured,
        status: draft.status,
        scheduled_for:
          draft.status === "scheduled" && draft.scheduled_for
            ? new Date(draft.scheduled_for).toISOString()
            : null,
        published_at: draft.status === "published" ? new Date().toISOString() : null,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_STORY });
      setCoverPreview("");
      notify(`${created.title} added.`);
    } catch {
      setError("We couldn't create that story. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.title, row.farmer_name || "", row.village || "", row.category || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = filtered.length > 0 && filtered.every((row) => selectedSet.has(row.id));

  const toggleSelect = (id: number) => {
    setSelectedIds((list) => (list.includes(id) ? list.filter((value) => value !== id) : [...list, id]));
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((list) => list.filter((id) => !filtered.some((row) => row.id === id)));
    } else {
      setSelectedIds((list) => Array.from(new Set([...list, ...filtered.map((row) => row.id)])));
    }
  };

  const bulkSetStatus = async (status: string, message: string) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setRows((list) => list.map((row) => (ids.includes(row.id) ? { ...row, status } : row)));
    try {
      await updateRows("farmer_stories", ids, {
        status,
        published_at: status === "published" ? new Date().toISOString() : null,
      });
      notify(message);
      setSelectedIds([]);
    } catch {
      setError("We couldn't update those stories. Please try again.");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const selected = rows.find((row) => row.id === selectedId) || null;
  const coverMap = useAssetUrlMap(selected?.cover_image ? [selected.cover_image] : []);
  const selectedCoverUrl = selected?.cover_image
    ? isStoragePath(selected.cover_image)
      ? coverMap[selected.cover_image]
      : selected.cover_image
    : null;

  const columns: Column<FarmerStory>[] = [
    {
      key: "title",
      header: "Story",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.title}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.farmer_name || "—"} · {row.village || "—"}</p>
        </div>
      ),
    },
    { key: "category", header: "Category", render: (row) => (
      <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">{row.category || "—"}</span>
    ) },
    { key: "read", header: "Read time", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.read_time || "—"}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...contentStatusMeta(STORY_STATUS, row.status, row.scheduled_for)} /> },
    { key: "gallery", header: "Gallery", render: (row) => <GalleryCell items={row.gallery} pathMap={galleryMap} /> },
    {
      key: "featured",
      header: "Featured",
      render: (row) =>
        row.featured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2 py-1 rounded-full">
            <i className="ri-star-fill"></i> Yes
          </span>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Farmer Stories"
        subtitle="The human stories behind the network — the growers, their families and their farms."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Story
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total stories", value: rows.length, icon: "ri-book-open-line", tone: "primary" },
          { label: "Published", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Featured", value: rows.filter((row) => row.featured).length, icon: "ri-star-line", tone: "accent" },
          { label: "Drafts", value: rows.filter((row) => row.status === "draft").length, icon: "ri-draft-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search stories by title, farmer, village…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(STORY_STATUS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} stories</span>
        </FilterBar>

        {selectedIds.length > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-foreground-800 flex-1">
              {selectedIds.length} stor{selectedIds.length === 1 ? "y" : "ies"} selected
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => bulkSetStatus("published", `${selectedIds.length} stor${selectedIds.length === 1 ? "y" : "ies"} published.`)}
                disabled={bulkBusy}
                className={btnPrimary}
              >
                {bulkBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-checkbox-circle-line"></i>} Publish
              </button>
              <button
                type="button"
                onClick={() => bulkSetStatus("draft", `${selectedIds.length} stor${selectedIds.length === 1 ? "y" : "ies"} moved to draft.`)}
                disabled={bulkBusy}
                className={btnGhost}
              >
                <i className="ri-draft-line"></i> Unpublish
              </button>
              <button type="button" onClick={() => setSelectedIds([])} className={btnGhost}>
                <i className="ri-close-line"></i> Clear
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <LoadingState label="Loading stories…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-book-open-line" title={rows.length === 0 ? "No stories yet" : "No stories match"} message="Farmer stories you publish will appear here." />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            onRowClick={(row) => setSelectedId(row.id)}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleAll}
            allSelected={allSelected}
          />
        )}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelectedId(null)} title={selected?.title || "Story"} subtitle="Farmer story" footer={selected ? (
        <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
          <i className="ri-delete-bin-line"></i> Delete story
        </button>
      ) : null}>
        {selected ? (
          <>
            {selectedCoverUrl ? (
              <div className="w-full h-40 rounded-lg overflow-hidden bg-background-100">
                <img src={selectedCoverUrl} alt={selected.title} className="w-full h-full object-cover object-top" />
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...contentStatusMeta(STORY_STATUS, selected.status, selected.scheduled_for)} />
              {selected.read_time ? (
                <span className="text-xs font-semibold text-foreground-700 bg-background-100 border border-background-300 px-2.5 py-1 rounded-full">{selected.read_time}</span>
              ) : null}
            </div>

            <DetailList
              items={[
                { icon: "ri-user-star-line", label: "Farmer", value: selected.farmer_name || "—" },
                { icon: "ri-plant-line", label: "Farm", value: selected.farm_name || "—" },
                { icon: "ri-map-pin-line", label: "Village", value: selected.village || "—" },
                {
                  icon: "ri-calendar-line",
                  label: selected.status === "scheduled" ? "Scheduled for" : "Published",
                  value:
                    selected.status === "scheduled"
                      ? formatSchedule(selected.scheduled_for)
                      : selected.published_at
                        ? formatDate(selected.published_at)
                        : "Not published",
                },
              ]}
            />

            {selected.excerpt ? (
              <Field label="Excerpt">
                <p className="text-sm text-foreground-700 leading-relaxed">{selected.excerpt}</p>
              </Field>
            ) : null}

            {selected.content ? (
              <Field label="Story preview">
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-6">{selected.content}</p>
              </Field>
            ) : null}

            <Field label="Photo gallery">
              <GalleryPicker
                label="Gallery photos"
                value={selected.gallery || []}
                onChange={(items) => update(selected.id, { gallery: items }, "Gallery updated.")}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Publication status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Story status updated.")}
                  className={fieldInput}
                >
                  {optionsFrom(STORY_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Featured">
                <button
                  type="button"
                  onClick={() => update(selected.id, { featured: !selected.featured }, selected.featured ? "Removed from featured." : "Marked as featured.")}
                  className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                    selected.featured ? "bg-accent-500 text-accent-950 border-accent-500" : "bg-background-100 text-foreground-700 border-background-300"
                  }`}
                >
                  <i className={selected.featured ? "ri-star-fill" : "ri-star-line"}></i>
                  {selected.featured ? "Featured" : "Not featured"}
                </button>
              </Field>
            </div>

            {selected.status === "scheduled" ? (
              <Field label="Publish automatically on">
                <input
                  type="datetime-local"
                  value={toDateTimeLocal(selected.scheduled_for)}
                  onChange={(event) =>
                    update(
                      selected.id,
                      {
                        scheduled_for: event.target.value
                          ? new Date(event.target.value).toISOString()
                          : null,
                      },
                      "Schedule updated.",
                    )
                  }
                  className={fieldInput}
                />
                <p className="text-xs text-foreground-500 mt-2">
                  This story goes live on the public journal automatically at this time.
                </p>
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Write a new farmer story"
        subtitle="New story"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createStory} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create story
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Story title">
            <input
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              placeholder="e.g. The Coconut Grove Keeper"
              className={textInput}
            />
          </TextField>
          <TextField label="Farmer">
            <input
              value={draft.farmer_name}
              onChange={(event) => setDraft({ ...draft, farmer_name: event.target.value })}
              placeholder="e.g. Kondaiah"
              className={textInput}
            />
          </TextField>
          <TextField label="Farm">
            <input
              value={draft.farm_name}
              onChange={(event) => setDraft({ ...draft, farm_name: event.target.value })}
              placeholder="e.g. Konaseema Coconut Farm"
              className={textInput}
            />
          </TextField>
          <TextField label="Village">
            <input
              value={draft.village}
              onChange={(event) => setDraft({ ...draft, village: event.target.value })}
              placeholder="e.g. Amalapuram"
              className={textInput}
            />
          </TextField>
          <TextField label="Category">
            <input
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              placeholder="e.g. Farm Life"
              className={textInput}
            />
          </TextField>
          <TextField label="Read time">
            <input
              value={draft.read_time}
              onChange={(event) => setDraft({ ...draft, read_time: event.target.value })}
              placeholder="e.g. 5 min read"
              className={textInput}
            />
          </TextField>
          <div className="sm:col-span-2">
            <CoverPicker
              label="Cover image"
              value={draft.cover_image}
              preview={coverPreview}
              onChange={(value, preview) => {
                setDraft({ ...draft, cover_image: value });
                setCoverPreview(preview);
              }}
            />
          </div>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(STORY_STATUS).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </TextField>
          <TextField label="Featured">
            <button
              type="button"
              onClick={() => setDraft({ ...draft, featured: !draft.featured })}
              className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                draft.featured
                  ? "bg-accent-500 text-accent-950 border-accent-500"
                  : "bg-background-100 text-foreground-700 border-background-300"
              }`}
            >
              <i className={draft.featured ? "ri-star-fill" : "ri-star-line"}></i>
              {draft.featured ? "Featured" : "Not featured"}
            </button>
          </TextField>
          {draft.status === "scheduled" ? (
            <div className="sm:col-span-2">
              <TextField label="Publish automatically on">
                <input
                  type="datetime-local"
                  value={draft.scheduled_for}
                  onChange={(event) => setDraft({ ...draft, scheduled_for: event.target.value })}
                  className={fieldInput}
                />
              </TextField>
            </div>
          ) : null}
          <div className="sm:col-span-2">
            <GalleryPicker
              label="Photo gallery"
              value={draft.gallery}
              onChange={(items) => setDraft({ ...draft, gallery: items })}
            />
          </div>
          <div className="sm:col-span-2">
            <TextField label="Excerpt">
              <textarea
                value={draft.excerpt}
                onChange={(event) => setDraft({ ...draft, excerpt: event.target.value })}
                rows={2}
                maxLength={500}
                placeholder="A short summary shown on the stories index…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
          <div className="sm:col-span-2">
            <TextField label="Story">
              <textarea
                value={draft.content}
                onChange={(event) => setDraft({ ...draft, content: event.target.value })}
                rows={5}
                placeholder="Tell the farmer's story…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
        </div>
      </Modal>
      {node}
    </div>
  );
}